import sizes, { default as getSizes } from "@/lib/utils/sizes";
import { ArticleStoryblok, MediaCardStoryblok } from "@/types/blok-types";
import clsx from "clsx";
import Link from "next/link";
import { PropsWithChildren } from "react";
import { StoryblokStory } from "storyblok-generate-ts";

import StoryblokComponent from "@/lib/storyblok/storyblokSetup/StoryblokComponent/StoryblokComponent";
import { BlokData } from "@/srpc/routes/story/story.router";
import styles from "./article-card.module.css";

const ArticleCard = ({
  imageBlok,
  sizes = getSizes(100, 50),
  heading,
  fullslug,
  description,
  linkOnImage,
  tag,
  slideshow,
  preview,
}: {
  imageBlok: MediaCardStoryblok | undefined;
  sizes: string;
  heading?: string;
  slug: string;
  fullslug: string;
  description: string | undefined;
  linkOnImage: boolean | undefined;
  tag: string | undefined;
  preview?: boolean;
  slideshow?: boolean;
}) => {
  const Root = ({ children }: PropsWithChildren) => {
    if (linkOnImage === true)
      return (
        <div className="article-card__root">
          <div className={clsx(styles.root, "article-card")}>{children}</div>
        </div>
      );
    return (
      <Link
        href={fullslug.startsWith("/") ? fullslug : `/${fullslug}`}
        className="article-card__root"
      >
        <div className={clsx(styles.root, "article-card")}>{children}</div>
      </Link>
    );
  };
  const Heading = () =>
    !heading ? null : (
      <p className={clsx(styles.heading, "article-card__heading")}>
        <span>{heading}</span>
      </p>
    );
  const CardImage = () => {
    if (!imageBlok) return null;

    const Tag = () => {
      if (!tag) return null;
      return (
        <div className={clsx(styles.tag, "article-card__tag")}>
          <span>{tag}</span>
        </div>
      );
    };

    if (linkOnImage === true)
      return (
        <Link
          href={fullslug}
          className={clsx(styles.image, "article-card__image")}
        >
          <Tag />
          <StoryblokComponent
            blok={imageBlok}
            sizes={sizes}
            preview={preview}
          />
        </Link>
      );
    return (
      <div className={clsx(styles.image, "article-card__image")}>
        <Tag />
        <StoryblokComponent blok={imageBlok} sizes={sizes} preview={preview} />
      </div>
    );
  };
  const Description = () =>
    !description ? null : (
      <p
        className={clsx(
          styles.description,
          "article-card__description",
          "line-clamp-3"
        )}
      >
        <span>{description}</span>
      </p>
    );

  return (
    <Root>
      <CardImage />
      <div
        className={clsx("article-card__content", slideshow && styles.slideshow)}
      >
        <Heading />
        <Description />
      </div>
    </Root>
  );
};

export const isArticleCard = (
  card: unknown
): card is StoryblokStory<ArticleStoryblok> => {
  if (!card) return false;

  return (
    (card as StoryblokStory<ArticleStoryblok>).content?.Title !== undefined
  );
};
const Card = ({
  linkOnImage,
  article,
  preview,
  slideshow,
}: {
  linkOnImage?: boolean;
  article: StoryblokStory<ArticleStoryblok>;
  preview?: boolean;
  slideshow?: boolean;
}) => {
  return !article ? null : (
    <ArticleCard
      imageBlok={article?.content?.listingImage?.[0]}
      sizes={sizes(100, 25)}
      heading={article?.content?.Title}
      key={article?.uuid}
      slug={article?.slug}
      description={article?.content?.shortDescription}
      fullslug={article?.full_slug}
      linkOnImage={linkOnImage}
      tag={article?.content?.category}
      preview={preview}
      slideshow={slideshow}
    />
  );
};

/**
 * @param linkOnImage - if set, only the image is a link instead of the whole card
 */
export const ArticleCardBlok = ({
  linkOnImage,
  data,
}: {
  linkOnImage?: boolean;
  data: BlokData["articleCard"];
}) => {
  if (!data) return null;
  return <Card linkOnImage={linkOnImage} article={data} />;
};

export default ArticleCard;

export { Card };
